// Republic Atlas - Header Component

import { Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Bell, Menu, X } from 'lucide-react';
import { cn } from '@/utils';
import { useAuthStore } from '@/store';
import SearchBar from './SearchBar';

const navLinks = [
  { href: '/', label: 'Home' },
  { href: '/elections', label: 'Elections' },
  { href: '/search', label: 'Search' },
  { href: '/pricing', label: 'Pricing' },
];

export function Header() {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isAuthenticated, user } = useAuthStore();

  return (
    <header className="sticky top-0 z-50 bg-surface/95 backdrop-blur-xl border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-9 h-9 bg-gradient-to-br from-primary via-primary-600 to-primary-700 rounded-lg flex items-center justify-center font-bold text-lg shadow-glow transition-all group-hover:shadow-glow group-hover:scale-105">
              RA
            </div>
            <span className="hidden sm:inline font-bold text-lg tracking-tight">
              Republic Atlas
            </span>
          </Link>

          {/* Search Bar (Desktop) */}
          <div className="hidden md:flex flex-1 max-w-xl mx-8">
            <div className="relative w-full">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type="text"
                placeholder="Search races, candidates, states..."
                className="w-full bg-surface-elevated border border-white/5 rounded-xl pl-10 pr-4 py-2.5 text-sm placeholder:text-gray-500 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-all"
              />
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={cn(
                  'px-3.5 py-2 text-sm font-medium rounded-lg transition-all',
                  location.pathname === link.href
                    ? 'text-white bg-primary/20 shadow-glow'
                    : 'text-gray-400 hover:text-white hover:bg-surface-hover'
                )}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-3">
            {/* Notifications (Premium+) */}
            {isAuthenticated && (
              <button
                className="p-2 text-gray-400 hover:text-white hover:bg-surface-hover rounded-lg transition-all"
                aria-label="Notifications"
              >
                <Bell className="w-5 h-5" />
              </button>
            )}

            {isAuthenticated ? (
              <Link 
                to="/account" 
                className="flex items-center gap-2.5 px-3 py-1.5 hover:bg-surface-hover rounded-lg transition-all group"
                title="Account Settings"
              >
                <span className="text-sm text-gray-300 hidden sm:block group-hover:text-white transition-colors">
                  {user?.displayName || user?.email?.split('@')[0]}
                </span>
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary/30 to-primary/20 flex items-center justify-center ring-2 ring-transparent group-hover:ring-primary/30 transition-all">
                  {user?.profileImageUrl ? (
                    <img
                      src={user.profileImageUrl}
                      alt={user.displayName || 'User'}
                      className="w-8 h-8 rounded-full"
                    />
                  ) : (
                    <span className="text-primary text-sm font-semibold">
                      {user?.displayName?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
                    </span>
                  )}
                </div>
              </Link>
            ) : (
              <Link
                to="/login"
                className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-600 rounded-lg transition-all shadow-glow hover:shadow-glow"
              >
                Sign in
              </Link>
            )}

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 text-gray-400 hover:text-white hover:bg-surface-hover rounded-lg transition-all"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden py-4 border-t border-white/5 overflow-hidden"
            >
              <nav className="flex flex-col gap-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={cn(
                      'px-4 py-3 text-base font-medium rounded-lg transition-all',
                      location.pathname === link.href
                        ? 'text-white bg-primary/20'
                        : 'text-gray-300 hover:text-white hover:bg-surface-hover'
                    )}
                  >
                    {link.label}
                  </Link>
                ))}
                {/* Account link for mobile */}
                {isAuthenticated && (
                  <Link
                    to="/account"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={cn(
                      'px-4 py-3 text-base font-medium rounded-lg transition-all',
                      location.pathname === '/account'
                        ? 'text-white bg-primary/20'
                        : 'text-gray-300 hover:text-white hover:bg-surface-hover'
                    )}
                  >
                    Account
                  </Link>
                )}
              </nav>
              <div className="mt-4 px-4">
                <SearchBar />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
}

export default Header;

